import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FriendProvider } from './context/FriendContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Calendar from './pages/Calendar';
import GrandPrix from './pages/GrandPrix';
import Pronostic from './pages/Pronostic';
import Leaderboard from './pages/Leaderboard';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import MyDriver from './pages/MyDriver';
import Standings from './pages/Standings';
import DriverPage from './pages/DriverPage';
import SeasonChampion from './pages/SeasonChampion';
import Players from './pages/Players';
import PlayerProfile from './pages/PlayerProfile';
import LightSpeed from './pages/LightSpeed';
import BoxBoxHub from './pages/BoxBoxHub';
import Friends from './pages/Friends';
import Quiz from './pages/Quiz';
import PodiumQuiz from './pages/PodiumQuiz';
import GrilleDeDepart from './pages/GrilleDeDepart';
import WhoAmI from './pages/WhoAmI';
import LeGap from './pages/LeGap';
import QuiMenait from './pages/QuiMenait';
import Paddock from './pages/Paddock';
import TheRace from './pages/TheRace';
import Ecuries from './pages/Ecuries';
import EcurieSalon from './pages/EcurieSalon';
import { MentionsLegales, Privacy, CGU, Contact } from './pages/Legal';
import GradeUpOverlay from './components/GradeUpOverlay';
import PageTransition from './components/PageTransition';
import DailyGoals from './components/DailyGoals';
import SeasonDriverModal from './components/SeasonDriverModal';
import { getLevelInfo, getGrade, GRADES } from './utils/xpUtils';
import api from './services/api';
import Icon from './components/Icon';

const ScrollProgressBar = () => {
  const [p, setP] = useState(0);
  useEffect(() => {
    const upd = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      setP(scrollHeight <= clientHeight ? 0 : (scrollTop / (scrollHeight - clientHeight)) * 100);
    };
    window.addEventListener('scroll', upd, { passive: true });
    return () => window.removeEventListener('scroll', upd);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 h-[3px] z-[9999] bg-zinc-900/40">
      <div className="h-full bg-gradient-to-r from-f1red via-red-400 to-f1red"
        style={{ width: `${p}%`, transition: 'none' }} />
    </div>
  );
};

const CANCELLED_RACES = new Set(['Bahrain', 'Saudi Arabia']);
const findNextRace = (races) => {
  const now = new Date();
  return races
    .filter(r =>
      r.sessions?.race?.date &&
      new Date(`${r.sessions.race.date}T${r.sessions.race.time || '12:00:00Z'}`) > now &&
      !CANCELLED_RACES.has(r.circuit?.country)
    )
    .sort((a, b) => new Date(a.sessions.race.date) - new Date(b.sessions.race.date))[0] ?? null;
};

const FirstPronosticModal = ({ race, onClose }) => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [progress, setProgress] = useState(100);
  const AUTO_CLOSE = 12000;

  useEffect(() => { const t = setTimeout(() => setMounted(true), 30); return () => clearTimeout(t); }, []);
  useEffect(() => {
    const start = Date.now();
    const frame = () => {
      const pct = Math.max(0, 100 - ((Date.now() - start) / AUTO_CLOSE) * 100);
      setProgress(pct);
      if (pct > 0) requestAnimationFrame(frame);
    };
    const raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);
  useEffect(() => { const t = setTimeout(onClose, AUTO_CLOSE); return () => clearTimeout(t); }, [onClose]);

  return (
    <>
      <style>{`
        @keyframes fp-backdrop { from { opacity:0 } to { opacity:1 } }
        @keyframes fp-card {
          0%   { transform: scale(0.5) translateY(40px); opacity:0 }
          65%  { transform: scale(1.04) translateY(-4px); opacity:1 }
          82%  { transform: scale(0.98) translateY(2px) }
          100% { transform: scale(1) translateY(0); opacity:1 }
        }
        @keyframes fp-stripe { 0% { transform:scaleX(0) } 100% { transform:scaleX(1) } }
        @keyframes fp-in { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
        @keyframes fp-glow {
          0%,100% { text-shadow:0 0 20px #e10600,0 0 40px #e10600 }
          50%     { text-shadow:0 0 40px #e10600,0 0 80px #e10600,0 0 120px #e10600 }
        }
      `}</style>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
        style={{ animation:'fp-backdrop 0.3s ease forwards', background:'rgba(0,0,0,0.90)', backdropFilter:'blur(10px)' }}
        onClick={onClose}>
        <div className="relative w-full max-w-sm rounded-2xl overflow-hidden"
          style={{
            border:'2px solid #e10600',
            background:'linear-gradient(160deg,#0c0c10 0%,#e1060018 100%)',
            boxShadow:'0 0 60px #e1060050,0 0 120px #e1060020,inset 0 0 40px #e1060008',
            animation:'fp-card 0.65s cubic-bezier(0.34,1.56,0.64,1) 0.1s both',
          }}
          onClick={e => e.stopPropagation()}>

          {/* Racing stripe */}
          <div style={{ height:5, background:'linear-gradient(90deg,#e10600 0%,transparent 80%)',
            animation:'fp-stripe 0.6s 0.2s ease-out both', transformOrigin:'left' }} />

          {/* Checkered accent */}
          <div className="absolute top-0 right-0 flex opacity-20 pointer-events-none">
            {[...Array(6)].map((_,i) => (
              <div key={i} style={{ width:10, height:10, background: i%2===0 ? '#fff' : 'transparent' }} />
            ))}
          </div>

          <div className="px-7 pt-6 pb-5 text-center">
            <div className="mb-3 inline-flex items-center justify-center text-f1red"
              style={{ animation:'fp-card 0.8s cubic-bezier(0.34,1.56,0.64,1) 0.35s both' }}>
              <Icon name="flag" size={56} strokeWidth={1.5} />
            </div>

            <p className="text-[10px] font-black uppercase tracking-[0.35em] text-zinc-500 mb-2"
              style={{ animation:'fp-in 0.4s 0.55s ease both', opacity:0 }}>
              Pronostics ouverts
            </p>

            <h2 className="text-3xl font-black mb-1 leading-tight"
              style={{ color:'#e10600', animation:'fp-glow 2s 0.8s ease-in-out infinite, fp-in 0.5s 0.65s ease both', opacity:0 }}>
              {race.raceName}
            </h2>

            <p className="text-zinc-400 text-sm mb-1"
              style={{ animation:'fp-in 0.4s 0.8s ease both', opacity:0 }}>
              Round {race.round} · Prouve que t'as le nez
            </p>

            <p className="text-zinc-500 text-xs mb-6"
              style={{ animation:'fp-in 0.4s 0.9s ease both', opacity:0 }}>
              Les pronos ferment avant les qualifications.
            </p>

            <button
              onClick={() => { onClose(); navigate(`/grand-prix/${race._id}/pronostic`); }}
              className="w-full py-3.5 rounded-xl font-black text-white text-base transition-all hover:scale-[1.02] active:scale-[0.98] mb-2"
              style={{ background:'linear-gradient(135deg,#e10600 0%,#b80500 100%)',
                animation:'fp-in 0.4s 1s ease both', opacity:0 }}>
              Faire mes pronos →
            </button>
            <button onClick={onClose}
              className="w-full py-2.5 rounded-xl font-bold text-zinc-600 text-sm hover:text-zinc-400 transition-colors"
              style={{ animation:'fp-in 0.4s 1.1s ease both', opacity:0 }}>
              Plus tard
            </button>
          </div>

          {/* Countdown bar */}
          <div className="h-1 bg-zinc-800">
            <div className="h-full transition-none" style={{ width:`${progress}%`, background:'#e10600', opacity:0.6 }} />
          </div>
        </div>
      </div>
    </>
  );
};

const SeasonDriverChecker = () => {
  const { user, refreshUser } = useAuth();
  const [show, setShow] = useState(false);
  const [nextRace, setNextRace] = useState(null);
  const [showProno, setShowProno] = useState(false);

  useEffect(() => {
    if (!user) { setShow(false); return; }
    if (user.seasonDriver) { setShow(false); return; }
    if (user.favoriteDriver) { setShow(false); return; }
    const dismissed = sessionStorage.getItem('p1_sd_dismissed');
    if (!dismissed) setShow(true);
  }, [user?.id, user?.seasonDriver, user?.favoriteDriver]);

  const handleClose = () => {
    sessionStorage.setItem('p1_sd_dismissed', '1');
    setShow(false);
  };

  const handlePicked = useCallback(async () => {
    await refreshUser();
    setShow(false);
    try {
      const season = new Date().getFullYear();
      const res = await api.get(`/races?season=${season}`);
      const nr = findNextRace(res.data);
      if (nr) { setNextRace(nr); setShowProno(true); }
    } catch {}
  }, [refreshUser]);

  if (show) return <SeasonDriverModal onClose={handleClose} onPicked={handlePicked} />;
  if (showProno && nextRace) return <FirstPronosticModal race={nextRace} onClose={() => setShowProno(false)} />;
  return null;
};

const GradeChecker = () => {
  const { user } = useAuth();
  const [gradeUp, setGradeUp] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    const { level } = getLevelInfo(user.xp ?? 0);
    const grade = getGrade(level);
    const key = `p1_last_grade_${user.id}`;
    const lastGradeId = localStorage.getItem(key);
    if (lastGradeId && lastGradeId !== grade.id) {
      const lastIdx = GRADES.findIndex(g => g.id === lastGradeId);
      const curIdx  = GRADES.findIndex(g => g.id === grade.id);
      if (curIdx > lastIdx) setGradeUp({ grade, level });
    }
    localStorage.setItem(key, grade.id);
  }, [user?.xp, user?.id]);

  return gradeUp
    ? <GradeUpOverlay grade={gradeUp.grade} level={gradeUp.level} onClose={() => setGradeUp(null)} />
    : null;
};

const PronosticReminderChecker = () => {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [nextRace, setNextRace] = useState(null);
  const checkedForUser = React.useRef(null);

  useEffect(() => {
    if (!user) {
      checkedForUser.current = null;
      setShow(false);
      return;
    }
    if (checkedForUser.current === user.id) return;
    checkedForUser.current = user.id;

    const season = new Date().getFullYear();
    api.get(`/races?season=${season}`)
      .then(res => {
        const nr = findNextRace(res.data);
        if (!nr) return;
        api.get(`/pronostics/${nr._id}/me`)
          .then(() => {})
          .catch(() => {
            setNextRace(nr);
            setShow(true);
          });
      })
      .catch(() => {});
  }, [user?.id]);

  if (!show || !nextRace) return null;
  return (
    <FirstPronosticModal
      race={nextRace}
      onClose={() => setShow(false)}
    />
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-zinc-400">Chargement...</div>;
  return user ? children : <Navigate to="/login" replace />;
};

const App = () => (
  <AuthProvider>
    <BrowserRouter>
    <FriendProvider>
      <div className="min-h-screen flex flex-col">
        <ScrollProgressBar />
        <Navbar />
        <PageTransition />
        <SeasonDriverChecker />
        <PronosticReminderChecker />
        <GradeChecker />
        <DailyGoals />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/grand-prix/:id" element={<GrandPrix />} />
            <Route path="/grand-prix/:id/pronostic" element={<ProtectedRoute><Pronostic /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<Navigate to="/paddock?tab=classements" replace />} />
            <Route path="/paddock" element={<ProtectedRoute><Paddock /></ProtectedRoute>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/my-driver" element={<ProtectedRoute><MyDriver /></ProtectedRoute>} />
            <Route path="/standings" element={<Standings />} />
            <Route path="/driver/:number" element={<DriverPage />} />
            <Route path="/saison" element={<SeasonChampion />} />
            <Route path="/joueurs" element={<Navigate to="/paddock?tab=joueurs" replace />} />
            <Route path="/joueurs/:id" element={<ProtectedRoute><PlayerProfile /></ProtectedRoute>} />
            <Route path="/box-box" element={<ProtectedRoute><BoxBoxHub /></ProtectedRoute>} />
            <Route path="/lightspeed" element={<ProtectedRoute><LightSpeed /></ProtectedRoute>} />
            <Route path="/amis" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
            <Route path="/ecuries" element={<Ecuries />} />
            <Route path="/ecuries/:id" element={<EcurieSalon />} />
            <Route path="/quiz" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
            <Route path="/podium" element={<ProtectedRoute><PodiumQuiz /></ProtectedRoute>} />
            <Route path="/grille" element={<ProtectedRoute><GrilleDeDepart /></ProtectedRoute>} />
            <Route path="/guess" element={<ProtectedRoute><WhoAmI /></ProtectedRoute>} />
            <Route path="/gap" element={<ProtectedRoute><LeGap /></ProtectedRoute>} />
            <Route path="/lead" element={<ProtectedRoute><QuiMenait /></ProtectedRoute>} />
            <Route path="/the-race" element={<ProtectedRoute><TheRace /></ProtectedRoute>} />
            <Route path="/the-race/:id" element={<ProtectedRoute><TheRace /></ProtectedRoute>} />
            <Route path="/legal"   element={<MentionsLegales />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cgu"     element={<CGU />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </FriendProvider>
    </BrowserRouter>
  </AuthProvider>
);

export default App;
